import { Input, type InputNumberProps } from 'antd';
import { useField } from 'formik';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FormItem, { type FormItemProps } from './FormItem';
import { InputNumber } from './InputNumberField';

const InputGroup = styled(Input.Group)`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

export interface InputFieldProps extends Omit<InputNumberProps, 'value' | 'onChange' | 'autoComplete'> {
    name: string;
    label?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    tooltip?: FormItemProps['tooltip'];
}

const InputRangeNumberField = ({ name, required, label, itemProps, tooltip, ...props }: InputFieldProps) => {
    const { t } = useTranslation(['common']);

    // from field handler
    const [fromField, fromMeta, fromHelper] = useField({ name: `${name}.from` });
    // to field handler
    const [toField, toMeta, toHelper] = useField({ name: `${name}.to` });

    const meta = useMemo(
        () => ({
            error: fromMeta.error || toMeta.error,
            touched: fromMeta.touched && toMeta.touched,
            initialValue: [fromMeta.initialValue, toMeta.initialValue],
            value: [fromMeta.value, toMeta.value],
        }),
        [
            fromMeta.error,
            fromMeta.initialValue,
            fromMeta.touched,
            fromMeta.value,
            toMeta.error,
            toMeta.initialValue,
            toMeta.touched,
            toMeta.value,
        ]
    );

    return (
        <FormItem {...itemProps} label={label} meta={meta} required={required} tooltip={tooltip}>
            <InputGroup>
                <InputNumber
                    autoComplete="off"
                    // jjspread props
                    {...props}
                    // then spread the field properties itself
                    {...fromField}
                    // manually attach on change
                    controls={false}
                    onChange={value => fromHelper.setValue(value)}
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>{t('common:rangeText')}</label>
                <InputNumber
                    autoComplete="off"
                    // jjspread props
                    {...props}
                    // then spread the field properties itself
                    {...toField}
                    // manually attach on change
                    controls={false}
                    onChange={value => toHelper.setValue(value)}
                />
            </InputGroup>
        </FormItem>
    );
};

export default memo(InputRangeNumberField);
